import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { ImportForhandsvisning, ImportKolonne } from '@/services/api/types';

export type { ImportKolonne };

interface Props<R> {
  data: ImportForhandsvisning<R>;
  kolonner: ImportKolonne[];
  /** Valgte radnr (bare rader uten feil kan velges). */
  valgte: Set<number>;
  /** Radnr som kan velges, i filrekkefølge. */
  velgbare: number[];
  /** Radnr med feil; vises uten avkrysningsboks. */
  feilRadnr: Set<number>;
  onVeksleRad: (radnr: number) => void;
  onVeksleAlle: () => void;
  oppsummering?: (forhandsvisning: ImportForhandsvisning<R>, valgte: ImportForhandsvisning<R>['rader']) => React.ReactNode;
}

const alleStatus = (antallValgte: number, antallVelgbare: number): boolean | 'indeterminate' => {
  if (antallVelgbare > 0 && antallValgte === antallVelgbare) return true;
  return antallValgte > 0 ? 'indeterminate' : false;
};

const StatusBadge = ({ feil, ny }: { feil: boolean; ny: boolean }) => {
  if (feil) return <Badge variant="destructive">Feil</Badge>;
  return <Badge variant={ny ? 'default' : 'secondary'}>{ny ? 'Ny' : 'Finnes'}</Badge>;
};

const ImportForhandsvisningTabell = <R,>({
  data,
  kolonner,
  valgte,
  velgbare,
  feilRadnr,
  onVeksleRad,
  onVeksleAlle,
  oppsummering,
}: Props<R>) => {
  const valgteRader = data.rader.filter((r) => valgte.has(r.radnr));
  return (
    <div className="min-w-0 max-w-full space-y-2">
      {oppsummering && <p className="text-sm font-medium">{oppsummering(data, valgteRader)}</p>}
      {data.rader.length > 0 && (
        <div data-testid="import-tabellramme" className="max-h-72 w-full min-w-0 max-w-full overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <Checkbox
                    aria-label="Velg alle rader"
                    checked={alleStatus(valgte.size, velgbare.length)}
                    disabled={velgbare.length === 0}
                    onCheckedChange={onVeksleAlle}
                  />
                </TableHead>
                <TableHead>Rad</TableHead>
                {kolonner.map((k) => (
                  <TableHead key={k.felt}>{k.overskrift}</TableHead>
                ))}
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rader.map((rad) => {
                const harFeil = feilRadnr.has(rad.radnr);
                return (
                  <TableRow key={rad.radnr}>
                    <TableCell>
                      {!harFeil && (
                        <Checkbox
                          aria-label={`Velg rad ${rad.radnr}`}
                          checked={valgte.has(rad.radnr)}
                          onCheckedChange={() => onVeksleRad(rad.radnr)}
                        />
                      )}
                    </TableCell>
                    <TableCell>{rad.radnr}</TableCell>
                    {kolonner.map((k) => (
                      <TableCell key={k.felt}>{String((rad as Record<string, unknown>)[k.felt] ?? '')}</TableCell>
                    ))}
                    <TableCell>
                      <StatusBadge feil={harFeil} ny={rad.status === 'ny'} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ImportForhandsvisningTabell;
